import { FC, useCallback, useEffect, useRef } from 'react';

import {
  Login,
  Register,
  Activation,
  ChangePassword,
  TOnboardRoutes,
  Modal,
  useModal,
} from '@skillarea/lib-frontend';
import { getCookie, MFA_PENDING_COOKIE_NAME } from '@skillarea/lib-shared';
import { useLocation, useNavigate } from '@tanstack/react-router';

import {
  getOnboardHashRoute,
  isOnboardModalHash,
} from '~/hooks/useOnboardModal';
import { useOnboardText } from '~/hooks/texts/useOnboardTexts';
import { onboardStore, useOnboardStore } from '~/stores/onboardStore';
import { consumePendingAuthRedirect } from '~/lib/analytics/auth-redirect';
import { pushGa4Event, pushCustomEvent } from '~/lib/analytics/gtm';

export const OnboardModal: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { closeModal, openModal } = useModal();
  const texts = useOnboardText();
  const lastRouteRef = useRef<string | null>(null);
  const lastActivatedHashRef = useRef<string | null>(null);
  const isLoggedIn = useOnboardStore((s) => s.isLoggedIn);
  const currentHashRoute = getOnboardHashRoute(location.hash);
  const isOnboardHashRoute = isOnboardModalHash(location.hash);
  const isMfaPending =
    !isLoggedIn && Boolean(getCookie(MFA_PENDING_COOKIE_NAME));

  const handleActivationSuccess = useCallback(
    (method: string, activationLink: string) => {
      if (!activationLink || lastActivatedHashRef.current === activationLink) {
        return;
      }

      lastActivatedHashRef.current = activationLink;
      pushGa4Event('sign_up', { method });
    },
    [],
  );

  const handleLoginSuccess = useCallback(
    (
      method: string,
      options?: {
        isMfaVerification?: boolean;
        mfaPendingCookieValue?: string | null;
      },
    ) => {
      if (options?.isMfaVerification) {
        const pendingAuthRedirect = consumePendingAuthRedirect(
          options.mfaPendingCookieValue ?? null,
        );

        if (pendingAuthRedirect) {
          pushGa4Event(pendingAuthRedirect.authEvent, {
            method: pendingAuthRedirect.authMethod,
          });
          return;
        }
      }

      pushGa4Event('login', { method });
    },
    [],
  );

  // Redirect hash routes to clean path if logged in
  useEffect(() => {
    if (isLoggedIn && isOnboardHashRoute) {
      navigate({ to: location.pathname, hash: '', replace: true });
    }
  }, [isLoggedIn, isOnboardHashRoute, location.pathname, navigate]);

  useEffect(() => {
    if (currentHashRoute !== 'activate') {
      lastActivatedHashRef.current = null;
    }
  }, [currentHashRoute]);

  // Support both hash routing (#login) and path routing (/change/:link)
  const rawRoute =
    location.hash.split('/')[0].replace('#', '') || // For hash routes (#login, #register)
    location.pathname.split('/')[1]; // For path routes (/change/:link)
  const route = isMfaPending ? 'login' : rawRoute;

  const handleBeforeClose = useCallback(() => {
    if (location.hash) {
      navigate({
        to: location.pathname,
        hash: '',
        replace: true,
      });
      return;
    }

    if (location.pathname.startsWith('/change/')) {
      navigate({
        to: '/',
        replace: true,
      });
    }
  }, [location.hash, location.pathname, navigate]);

  useEffect(() => {
    const validRoutes = ['login', 'register', 'activate', 'change', 'restore'];
    const isValidRoute = validRoutes.includes(route);

    // Close modal if logged in or invalid route
    if (isLoggedIn || !isValidRoute) {
      closeModal();
      lastRouteRef.current = null;
      return;
    }

    // Skip if modal already opened for this route
    if (lastRouteRef.current === route) {
      return;
    }

    lastRouteRef.current = route;

    // Get content based on current route
    const getContent = () => {
      switch (route) {
        case 'login':
        case 'restore':
          return (
            <Login
              key={route}
              mode={route as TOnboardRoutes}
              oauthTexts={texts.oauth}
              onLoginSuccess={handleLoginSuccess}
              onboardStore={onboardStore}
              texts={texts.login}
            />
          );

        case 'register':
          return (
            <Register
              key={route}
              oauthTexts={texts.oauth}
              onRegisterSuccess={() => pushCustomEvent('submit_lead_form')}
              onboardStore={onboardStore}
              texts={texts.register}
            />
          );

        case 'activate':
          return (
            <Activation
              key={route}
              onActivationSuccess={handleActivationSuccess}
              onboardStore={onboardStore}
              texts={texts.activation}
            />
          );

        case 'change':
          return (
            <ChangePassword
              key={route}
              onboardStore={onboardStore}
              texts={texts.changePassword}
            />
          );

        default:
          return (
            <Login
              key="default"
              mode={'login' as TOnboardRoutes}
              oauthTexts={texts.oauth}
              onLoginSuccess={handleLoginSuccess}
              onboardStore={onboardStore}
              texts={texts.login}
            />
          );
      }
    };

    const getTitle = (): string => {
      switch (route) {
        case 'login':
          return texts.login.logInTitle;
        case 'restore':
          return texts.login.restoreTitle;
        case 'register':
          return texts.register.title;
        case 'activate':
          return texts.activation.title;
        case 'change':
          return texts.changePassword.title;
        default:
          return 'Modal';
      }
    };

    // Wrap modal content in a stable container for e2e selectors
    openModal(
      <div data-testid="onboard-modal">
        <Modal
          hideCloseButton={isMfaPending}
          onBeforeClose={handleBeforeClose}
          preventOutsideClose={isMfaPending}
          title={getTitle()}
        >
          {getContent()}
        </Modal>
      </div>,
      {},
      true,
    );
  }, [
    isLoggedIn,
    route,
    handleBeforeClose,
    closeModal,
    openModal,
    texts,
    isMfaPending,
    handleActivationSuccess,
    handleLoginSuccess,
  ]);

  return null;
};
